import { useEffect, useState } from "react";
import { DollarSign, TrendingUp, ShieldCheck, Crown, Wrench, ArrowRightLeft, Calendar } from "lucide-react";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { PortalShell } from "@/components/PortalShell";

const KIND_META = {
  water_test:        { label: "Water test",        icon: ShieldCheck,    color: "bg-cyan-100 text-cyan-700" },
  membership:        { label: "Membership",        icon: Crown,          color: "bg-violet-100 text-violet-700" },
  property_transfer: { label: "Property transfer", icon: ArrowRightLeft, color: "bg-amber-100 text-amber-700" },
  service:           { label: "Service",           icon: Wrench,         color: "bg-blue-100 text-blue-700" },
};

const CONFIDENCE_PILL = {
  high:    { bg: "bg-emerald-100", text: "text-emerald-800", label: "High" },
  medium:  { bg: "bg-amber-100",   text: "text-amber-800",   label: "Medium" },
  low:     { bg: "bg-slate-100",   text: "text-slate-700",   label: "Low" },
};

const TYPE_LABEL = {
  conveyancer: "Conveyancer", real_estate_agent: "Real Estate Agent", buyer_agent: "Buyer Agent",
  property_manager: "Property Manager", pump_shop: "Pump Shop", plumber: "Plumber",
  building_inspector: "Building Inspector", water_treatment_provider: "Water Treatment", other: "Other",
};

function fmt(n) { return `$${(n || 0).toFixed(2)}`; }
function fmtDate(s) { return formatDateAU(s); }

export default function AdminAttribution() {
  const [dash, setDash] = useState(null);
  const [log, setLog] = useState({ rows: [], window_days: 60 });
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("");
  const [confFilter, setConfFilter] = useState("");
  const [showDemo, setShowDemo] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (kindFilter) params.revenue_kind = kindFilter;
    if (confFilter) params.confidence = confFilter;
    if (showDemo) params.show_demo = "true";
    const dashParams = showDemo ? { show_demo: "true" } : {};
    Promise.all([
      api.get("/admin/attribution/dashboard", { params: dashParams }).then(({ data }) => setDash(data)),
      api.get("/admin/attribution/log", { params }).then(({ data }) => setLog(data)),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kindFilter, confFilter, showDemo]);

  const totalRevenue = dash ? Object.values(dash.by_kind).reduce((s, v) => s + (v.revenue || 0), 0) : 0;
  const totalEvents = dash ? Object.values(dash.by_kind).reduce((s, v) => s + (v.count || 0), 0) : 0;

  return (
    <PortalShell>
      <div data-testid="admin-attribution-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-600">Financial Intelligence</div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">Revenue attribution</h1>
            <p className="text-slate-500 mt-1 text-sm">Every booking, membership and transfer auto-credited to the partner who introduced the property. {dash?.window_days || 60}-day last-touch window.</p>
          </div>
          <label
            data-testid="attribution-show-demo-toggle"
            className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50"
          >
            <input
              type="checkbox"
              checked={showDemo}
              onChange={(e) => setShowDemo(e.target.checked)}
              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              data-testid="attribution-show-demo-checkbox"
            />
            Show test data
          </label>
        </div>

        {showDemo && (
          <div
            data-testid="attribution-forensic-banner"
            className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <span className="font-semibold">Forensic view:</span> demo, fixture and pytest-seeded
            referrals are included. Switch off to return to the live operational view.
          </div>
        )}

        {/* Top metrics */}
        {dash && (
          <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="attribution-metrics">
            <Metric label="Total revenue attributed" value={fmt(totalRevenue)} hint={`${totalEvents} revenue event(s)`} icon={DollarSign} accent="emerald" />
            <Metric label="Top partner" value={fmt(dash.top_revenue_partners[0]?.revenue || 0)} hint={dash.top_revenue_partners[0]?.business_name || dash.top_revenue_partners[0]?.email || "—"} icon={TrendingUp} accent="cyan" />
            <Metric label="High-confidence" value={dash.by_confidence?.high?.count || 0} hint={fmt(dash.by_confidence?.high?.revenue || 0)} icon={ShieldCheck} accent="emerald" />
            <Metric label="Membership revenue" value={fmt(dash.by_kind?.membership?.revenue || 0)} hint={`${dash.by_kind?.membership?.count || 0} memberships`} icon={Crown} accent="violet" />
          </div>
        )}

        {/* By revenue kind */}
        {dash && (
          <div className="mt-7 grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-200 p-6">
              <h2 className="font-display font-semibold text-slate-900 mb-4">Top revenue partners</h2>
              {dash.top_revenue_partners.length === 0 ? (
                <p className="text-sm text-slate-500">No attributions yet. Partners will appear here once a referred customer makes a paid booking or starts a membership.</p>
              ) : (
                <ol className="space-y-2.5">
                  {dash.top_revenue_partners.map((p, i) => (
                    <li key={p.id} className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-100">
                      <div className="w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 truncate">{p.business_name || p.email}</div>
                        <div className="text-xs text-slate-500">{TYPE_LABEL[p.partner_type] || p.partner_type}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-xl font-bold text-emerald-700">{fmt(p.revenue)}</div>
                        <div className="text-[10px] text-slate-500">{p.count} event{p.count !== 1 ? "s" : ""}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="bg-white rounded-3xl border border-slate-200 p-6">
              <h2 className="font-display font-semibold text-slate-900 mb-4">By revenue type</h2>
              <ul className="space-y-2 text-sm">
                {Object.entries(dash.by_kind).map(([k, v]) => {
                  const meta = KIND_META[k] || KIND_META.water_test;
                  const Icon = meta.icon;
                  return (
                    <li key={k} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.color}`}><Icon className="w-4 h-4" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900">{meta.label}</div>
                        <div className="text-xs text-slate-500">{v.count} event{v.count !== 1 ? "s" : ""}</div>
                      </div>
                      <div className="font-mono text-slate-900 font-bold">{fmt(v.revenue)}</div>
                    </li>
                  );
                })}
                {Object.keys(dash.by_kind).length === 0 && <li className="text-sm text-slate-500">No attributions yet.</li>}
              </ul>

              <h2 className="font-display font-semibold text-slate-900 mt-6 mb-3">By partner type</h2>
              <ul className="space-y-1.5 text-sm">
                {Object.entries(dash.by_partner_type).slice(0, 6).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                    <span className="text-slate-700">{TYPE_LABEL[k] || k}</span>
                    <span className="font-mono text-slate-900 font-semibold">{fmt(v.revenue)}</span>
                  </li>
                ))}
                {Object.keys(dash.by_partner_type).length === 0 && <li className="text-sm text-slate-500">—</li>}
              </ul>
            </div>
          </div>
        )}

        {/* By month */}
        {dash && dash.by_month.length > 0 && (
          <div className="mt-5 bg-white rounded-3xl border border-slate-200 p-6">
            <h2 className="font-display font-semibold text-slate-900 mb-4 flex items-center gap-2"><Calendar className="w-4 h-4 text-cyan-700" />Monthly revenue (last 12 months)</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5">
              {dash.by_month.map((m) => (
                <div key={m.month} className="rounded-xl bg-slate-50 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{m.month}</div>
                  <div className="font-display text-lg font-bold text-slate-900 mt-0.5">{fmt(m.revenue)}</div>
                  <div className="text-[10px] text-slate-500">{m.count} event{m.count !== 1 ? "s" : ""}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attribution log */}
        <div className="mt-7">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <h2 className="font-display text-xl font-bold text-slate-900">Attribution log</h2>
            <div className="flex items-center gap-2 text-xs">
              <select data-testid="attr-kind-filter" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="rounded-full border-slate-200 text-xs px-3 py-1.5">
                <option value="">All types</option>
                <option value="water_test">Water test</option>
                <option value="membership">Membership</option>
                <option value="property_transfer">Transfer</option>
              </select>
              <select data-testid="attr-conf-filter" value={confFilter} onChange={(e) => setConfFilter(e.target.value)} className="rounded-full border-slate-200 text-xs px-3 py-1.5">
                <option value="">All confidence</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-3">When</th>
                    <th className="text-left px-5 py-3">Partner</th>
                    <th className="text-left px-5 py-3">Property</th>
                    <th className="text-left px-5 py-3">Type</th>
                    <th className="text-left px-5 py-3">Confidence</th>
                    <th className="text-right px-5 py-3">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="text-center py-10 text-slate-500">Loading…</td></tr>
                  ) : log.rows.length === 0 ? (
                    <tr><td colSpan="6" className="text-center py-12 text-slate-500">
                      <DollarSign className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                      No attributions yet. Once a customer arrives via a partner-tagged link and pays for testing or joins the AWARE Water Health Record membership, the attribution will appear here automatically.
                    </td></tr>
                  ) : (
                    log.rows.map((r) => {
                      const meta = KIND_META[r.revenue_kind] || KIND_META.water_test;
                      const conf = CONFIDENCE_PILL[r.confidence] || CONFIDENCE_PILL.low;
                      const Icon = meta.icon;
                      return (
                        <tr key={r.id} data-testid={`attr-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                          <td className="px-5 py-3 text-slate-700 text-xs">{fmtDate(r.created_at)}</td>
                          <td className="px-5 py-3">
                            <div className="font-medium text-slate-900">{r.partner?.business_name || r.partner?.email || "—"}</div>
                            <div className="text-[11px] text-slate-500">{TYPE_LABEL[r.partner?.partner_type] || ""}</div>
                          </td>
                          <td className="px-5 py-3 text-slate-700">{r.property_address || "—"}</td>
                          <td className="px-5 py-3">
                            <span className="inline-flex items-center gap-1.5 text-xs text-slate-700"><Icon className="w-3.5 h-3.5" />{meta.label}</span>
                          </td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${conf.bg} ${conf.text}`}>{conf.label}</span>
                          </td>
                          <td className="px-5 py-3 text-right font-mono font-bold text-emerald-700">{fmt(r.revenue_generated)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}


function Metric({ icon: Icon, label, value, hint, accent }) {
  const styles = {
    emerald: "text-emerald-700 bg-emerald-50",
    cyan: "text-cyan-700 bg-cyan-50",
    violet: "text-violet-700 bg-violet-50",
  }[accent];
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-5">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${styles}`}><Icon className="w-4 h-4" /></div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{label}</div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-1">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1 truncate">{hint}</div>}
    </div>
  );
}
